/**
 * Performance optimization utilities
 */

/**
 * Throttle function to limit the rate of function calls
 * @param func - Function to throttle
 * @param limit - Time limit in milliseconds
 * @returns Throttled function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return function (this: any, ...args: Parameters<T>) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Debounce function to delay function calls until after a specified delay
 * @param func - Function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: number;
  return function (this: any, ...args: Parameters<T>) {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => func.apply(this, args), delay);
  };
}

/**
 * Memoize function results to avoid expensive recalculations
 * @param func - Function to memoize
 * @param getKey - Function to generate cache key from arguments
 * @returns Memoized function
 */
export function memoize<T extends (...args: any[]) => any>(
  func: T,
  getKey?: (...args: Parameters<T>) => string
): T {
  const cache = new Map<string, ReturnType<T>>();

  return ((...args: Parameters<T>): ReturnType<T> => {
    const key = getKey ? getKey(...args) : JSON.stringify(args);

    if (cache.has(key)) {
      return cache.get(key)!;
    }

    const result = func(...args);
    cache.set(key, result);
    return result;
  }) as T;
}

/**
 * Lazy load images with intersection observer
 * @param imageElement - Image element to lazy load
 * @param src - Image source URL
 * @param placeholder - Placeholder image URL
 */
export function lazyLoadImage(
  imageElement: HTMLImageElement,
  src: string,
  placeholder?: string
): void {
  if (placeholder) {
    imageElement.src = placeholder;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target as HTMLImageElement;
          img.src = src;
          img.onload = () => {
            img.classList.add('loaded');
          };
          observer.unobserve(img);
        }
      });
    },
    {
      rootMargin: '50px',
    }
  );

  observer.observe(imageElement);
}

/**
 * Preload critical resources
 * @param resources - Array of resource URLs to preload
 * @param type - Resource type ('script', 'style', 'image', etc.)
 */
export function preloadResources(
  resources: string[],
  type: 'script' | 'style' | 'image' | 'font' = 'script'
): void {
  resources.forEach((resource) => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource;

    switch (type) {
      case 'script':
        link.as = 'script';
        break;
      case 'style':
        link.as = 'style';
        break;
      case 'image':
        link.as = 'image';
        break;
      case 'font':
        link.as = 'font';
        link.crossOrigin = 'anonymous';
        break;
    }

    document.head.appendChild(link);
  });
}

/**
 * Check if user prefers reduced motion
 * @returns True if user prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Get device performance tier based on hardware capabilities
 * @returns Performance tier: 'high', 'medium', or 'low'
 */
export function getPerformanceTier(): 'high' | 'medium' | 'low' {
  // Check for hardware concurrency (CPU cores)
  const cores = navigator.hardwareConcurrency || 1;

  // Check for device memory (if available)
  const memory = (navigator as any).deviceMemory || 4;

  // Check for connection speed
  const connection = (navigator as any).connection;
  const effectiveType = connection?.effectiveType || '4g';

  // Determine tier based on capabilities
  if (cores >= 8 && memory >= 8 && (effectiveType === '4g' || !connection)) {
    return 'high';
  } else if (cores >= 4 && memory >= 4 && effectiveType !== 'slow-2g') {
    return 'medium';
  } else {
    return 'low';
  }
}

/**
 * Optimize images based on device capabilities
 * @param baseUrl - Base image URL
 * @param sizes - Available image sizes
 * @returns Optimized image URL
 */
export function getOptimizedImageUrl(
  baseUrl: string,
  sizes: { width: number; suffix: string }[]
): string {
  const tier = getPerformanceTier();
  const devicePixelRatio = window.devicePixelRatio || 1;

  // Adjust quality based on performance tier
  let targetWidth = 800; // Default width

  if (tier === 'high') {
    targetWidth = Math.min(1200, targetWidth * devicePixelRatio);
  } else if (tier === 'medium') {
    targetWidth = Math.min(800, targetWidth * Math.min(devicePixelRatio, 1.5));
  } else {
    targetWidth = Math.min(600, targetWidth);
  }

  // Find the best matching size
  const bestSize = sizes.reduce((best, current) => {
    const bestDiff = Math.abs(best.width - targetWidth);
    const currentDiff = Math.abs(current.width - targetWidth);
    return currentDiff < bestDiff ? current : best;
  });

  return baseUrl.replace(/\.[^.]+$/, `${bestSize.suffix}$&`);
}
